import { useCallback, useEffect, useRef, useState } from 'react';
export const useAudioPlayer = (audioUrl) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolumeState] = useState(75); // Default 75%
    const [isMuted, setIsMuted] = useState(false);
    const triedFallbackRef = useRef(false);
    // Simple fallback to get duration when audio element fails
    const getDurationFallback = async () => {
        const windowWithAudio = window;
        if (!window.AudioContext && !windowWithAudio.webkitAudioContext)
            return 0;
        try {
            const AudioContextClass = window.AudioContext || windowWithAudio.webkitAudioContext;
            const audioContext = new AudioContextClass();
            const response = await fetch(audioUrl);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            audioContext.close();
            return audioBuffer.duration;
        }
        catch (_) {
            return 0;
        }
    };
    // Initialize audio element
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        audio.src = audioUrl;
        audio.volume = volume / 100; // Only sets initial volume
        audio.load();
        triedFallbackRef.current = false; // Reset for new URL
        return () => {
            audio.pause();
            audio.src = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl]);
    // Cleanup on unmount
    useEffect(() => {
        const audio = audioRef.current;
        return () => {
            if (audio) {
                audio.pause();
                audio.src = '';
                audio.load(); // Force browser to release resources
                audio.currentTime = 0;
            }
        };
    }, []);
    // Handle time updates and duration
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };
        const handleLoadedMetadata = async () => {
            // Try normal way first
            if (!isNaN(audio.duration) &&
                audio.duration > 0 &&
                isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
            // Always fallback if normal way fails
            else if (!triedFallbackRef.current) {
                triedFallbackRef.current = true;
                const fallbackDuration = await getDurationFallback();
                if (fallbackDuration > 0) {
                    setDuration(fallbackDuration);
                }
            }
        };
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const play = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.play();
            setIsPlaying(true);
        }
    }, []);
    const pause = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            setIsPlaying(false);
        }
    }, []);
    const togglePlay = useCallback(() => {
        if (isPlaying) {
            pause();
        }
        else {
            play();
        }
    }, [isPlaying, play, pause]);
    const seek = useCallback((time) => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = time;
            setCurrentTime(time);
        }
    }, []);
    const setVolume = useCallback((level) => {
        const audio = audioRef.current;
        const clampedLevel = Math.max(0, Math.min(100, level));
        if (audio) {
            audio.volume = clampedLevel / 100;
            setVolumeState(clampedLevel);
            // Unmute if setting volume above 0
            if (clampedLevel > 0 && isMuted) {
                setIsMuted(false);
            }
        }
    }, [isMuted]);
    const toggleMute = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            const newMutedState = !isMuted;
            audio.muted = newMutedState;
            setIsMuted(newMutedState);
        }
    }, [isMuted]);
    return {
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        play,
        pause,
        togglePlay,
        seek,
        setVolume,
        toggleMute,
        audioRef,
    };
};
